/*
 * @providesModule CurrentUserStore
 *
 */

import FastImage from 'react-native-fast-image';
import FraudPrompt from 'SKZFraudPrompt';
import MinFlux from 'min-flux';
import GlobalEventEmitter from 'GlobalEventEmitter';
import NetClient from 'NetClient';
import Preferences from 'Preferences';
import RESTStore, {isWithinBackgroundResumeGracePeriod} from 'RESTStore';
import SKZConstants from 'SKZConstants';
import I18n from 'INTL';
import ClientPropertyConstants from 'ClientPropertyConstants';
import _ from 'lodash';
import {NativeModules, Platform} from 'react-native';
import APIUtilities from '../../Utilities/Network/APIUtilities';
import AssetHelper from 'AssetHelper';
import PhoneInput from '../../Utilities/PhoneInput/PhoneInput';

const UNREAD_SUPPORT_MESSAGES_COUNT = 'unreadSupportMessagesCountSKZ';
const TOTAL_GAMES_PLAYED = 'totalGamesPlayedSKZ';
const UNCLAIMED_TROPHIES_COUNT = 'trophiesAwardedAmountSKZ';
const SKZAPIManager = NativeModules.SKZAPIManager;
const SKZReactUserManager = NativeModules.SKZReactUserManager;
const SkillzModule = NativeModules.SkillzModule;
const ReactUserManager = NativeModules.SKZReactUserManager;
const DEFAULT_ERROR_MESSAGE = I18n.message("Uh oh, that didn't work. Please try again");

const gameId = SkillzModule.App.GameId;

const Store = new MinFlux.Store({
  _data: {},
  _hasPrefetched: false,

  async prefetchUserData(completion: () => {}): Promise {
    if (!this._hasPrefetched) {
      this._hasPrefetched = true;
      const randomAvatars = await this.fetchRandomAvatars();
      const formattedRandomAvatars = randomAvatars.map((image: string) => {
        return {uri: image};
      });
      FastImage.preload(formattedRandomAvatars);
    }
    completion();
  },

  async getCurrentUserId(): Promise {
    return new Promise((resolve: (Object) => {}, reject: (Object) => {}) => {
      SKZReactUserManager.getUserId((error: Object, userId: string) => {
        if (error) {
          console.warn('CurrentUserStore: error getting current user id', error);
        }
        resolve(userId);
      });
    });
  },

  fetchUser(useCache: boolean, failSilently?: boolean): Promise {
    return new Promise((resolve: (Object) => {}, reject: (Object) => {}) => {
      const errorHandleType = failSilently
        ? SKZAPIManager.OnError.failSilently
        : SKZAPIManager.OnError.promptRetry;
      if (useCache) {
        console.debug('CurrentUserStore: fetchUser: fetch user from cache');
        RESTStore.getAnyResponse(
          'user',
          null,
          errorHandleType,
          (response: Object, error: Object) => {
            if (error || !response) {
              reject(error);
            } else {
              console.debug(
                'CurrentUserStore fetchUser with cache, response: ' + JSON.stringify(response)
              );
              this.updateUserData(response);
              resolve(response);
            }
          }
        );
      } else {
        console.debug('CurrentUserStore: fetchUser: fetch user without cache');
        RESTStore.getServerResponse(
          'user',
          null,
          errorHandleType,
          (response: Object, error: Object) => {
            if (error || !response) {
              reject(error);
            } else {
              console.debug(
                'CurrentUserStore fetchUser without cache, response: ' + JSON.stringify(response)
              );
              this.updateUserData(response);
              resolve(response);
            }
          }
        );
      }
    });
  },

  fetchUserProfile(useCache: boolean): Promise {
    return new Promise((resolve: (Object) => {}, reject: (Object) => {}) => {
      this.getUserData(useCache)
        .then((user: Object) => {
          if (user.id) {
            const getResponse = useCache
              ? RESTStore.getAnyResponse.bind(RESTStore)
              : RESTStore.getServerResponse.bind(RESTStore);
            // Use failSilently when suppress_uhoh_dialog flag is enabled AND app is within background resume grace period
            const shouldFailSilently =
              ClientPropertyConstants.getSuppressUhOhDialog() &&
              isWithinBackgroundResumeGracePeriod();
            const errorHandleType = shouldFailSilently
              ? SKZAPIManager.OnError.failSilently
              : SKZAPIManager.OnError.promptRetry;
            getResponse(
              'users/' + user.id + '/profile',
              null,
              errorHandleType,
              (response: Object, error: Object) => {
                if (error || !response) {
                  reject(error);
                } else {
                  Preferences.set({[TOTAL_GAMES_PLAYED]: response.total_games_played});
                  Preferences.set({[UNCLAIMED_TROPHIES_COUNT]: response.trophies_awarded});
                  if (response.total_games_played >= 5) {
                    Preferences.set({[SKZConstants.MATCH_SELECT_SCROLLED_TO_MATCH_CODE]: true});
                  } else if (response.total_games_played <= 2) {
                    Preferences.set({[SKZConstants.MATCH_SELECT_SCROLLED_TO_MATCH_CODE]: false});
                  }
                  this._data.userProfile = response;
                  resolve(response);
                }
              }
            );
          } else {
            reject(DEFAULT_ERROR_MESSAGE);
          }
        })
        .catch(reject);
    });
  },

  fetchRandomAvatars(): Promise {
    return new Promise((resolve: (Object) => {}, reject: (Object) => {}) => {
      this.getUserData(true).then((user: Object) => {
        if (user.id) {
          RESTStore.getAnyResponse(
            'users/' + user.id + '/avatar_urls',
            null,
            SKZAPIManager.OnError.failSilently,
            (response: Object, error: Object) => {
              if (error || !response) {
                reject(error);
              } else {
                resolve(response);
              }
            }
          );
        } else {
          reject(DEFAULT_ERROR_MESSAGE);
        }
      });
    });
  },

  fetchRandomGeneratedUsername(): Promise {
    return new Promise((resolve: (username: string) => void, reject: (reason?: any) => void) => {
      this.getUserData(true).then((user: Object) => {
        if (!user.id) {
          reject(DEFAULT_ERROR_MESSAGE);
          return;
        }

        RESTStore.getServerResponse(
          '/user/v1/random-name',
          null,
          SKZAPIManager.OnError.failSilently,
          (response: string, error: Error | null) => {
            if (error || !response) {
              reject(error);
              return;
            }

            const username = response.name;
            if (username) {
              resolve(username);
            } else {
              reject('Empty or invalid response body for v1/random-name');
            }
          }
        );
      });
    });
  },

  fetchUserPersonalInfo(useCache: boolean): Promise {
    return new Promise((resolve: (Object) => {}, reject: (Object) => {}) => {
      this.getUserData(useCache).then((user: Object) => {
        if (user?.id) {
          // Use failSilently when suppress_uhoh_dialog flag is enabled AND app is within background resume grace period
          const shouldFailSilently =
            ClientPropertyConstants.getSuppressUhOhDialog() &&
            isWithinBackgroundResumeGracePeriod();
          const errorHandleType = shouldFailSilently
            ? SKZAPIManager.OnError.failSilently
            : SKZAPIManager.OnError.promptRetry;
          if (useCache) {
            console.debug('CurrentUserStore fetch user personal from cache');
            RESTStore.getAnyResponse(
              'users/' + user.id + '/personal',
              null,
              errorHandleType,
              (response: Object, error: Object) => {
                if (error || !response) {
                  reject(error);
                } else {
                  resolve(response);
                }
              }
            );
          } else {
            console.debug('CurrentUserStore fetch user personal without cache');
            RESTStore.getServerResponse(
              'users/' + user?.id + '/personal',
              null,
              errorHandleType,
              (response: Object, error: Object) => {
                if (error || !response) {
                  reject(error);
                } else {
                  resolve(response);
                }
              }
            );
          }
        } else {
          console.error('User id object is null, unable to fetch personal info.');
          reject(DEFAULT_ERROR_MESSAGE);
        }
      });
    });
  },

  fetchUserFriendInvitationInfo(useCache: boolean): Promise {
    return new Promise((resolve: (Object) => {}, reject: (Object) => {}) => {
      this.getUserData(true)
        .then((user: Object) => {
          if (user.id) {
            if (useCache) {
              console.debug('CurrentUserStore fetch user friend invitation info from cache');
              RESTStore.getAnyResponse(
                'users/' + user.id + '/friend_invitation',
                null,
                SKZAPIManager.OnError.promptRetry,
                (response: Object, error: Object) => {
                  if (error || !response) {
                    reject(error);
                  } else {
                    resolve(response);
                  }
                }
              );
            } else {
              console.debug('CurrentUserStore fetch user friend invitation info without cache');
              RESTStore.getServerResponse(
                'users/' + user.id + '/friend_invitation',
                null,
                SKZAPIManager.OnError.promptRetry,
                (response: Object, error: Object) => {
                  if (error || !response) {
                    reject(error);
                  } else {
                    resolve(response);
                  }
                }
              );
            }
          } else {
            reject(DEFAULT_ERROR_MESSAGE);
          }
        })
        .catch((error: unknown) => {
          reject(error);
        });
    });
  },

  isSavedAccount(): boolean {
    const personalInfo = this.getCurrentCachedPersonalInfo();
    const userData = this.getCurrentCachedUserData();
    return Boolean(userData?.email || (personalInfo?.phone_number && personalInfo?.phone_verified));
  },

  isEmailVerified(): boolean {
    const userData = this.getCurrentCachedUserData();
    return Boolean(userData?.email_verified);
  },

  updateUserData(userData: Object) {
    this._data.userData = userData;
    console.debug('CurrentUserStore updateUserData: ' + JSON.stringify(userData));
    if (userData && userData.id) {
      console.debug('CurrentUserStore will set unreadCount');
      Preferences.set({[UNREAD_SUPPORT_MESSAGES_COUNT]: userData.unread_support_message_count});
      if (Platform.OS === 'ios') {
        console.debug('CurrentUserStore will update core data');
        SkillzModule.updateCoreDataUserInfo(userData);
        return;
      }
      const methodExists = typeof SkillzModule.updateAndroidUserInfo === 'function';
      if (Platform.OS === 'android' && methodExists) {
        console.debug('CurrentUserStore will update android data');
        SkillzModule.updateAndroidUserInfo(userData);
      }
    }
  },

  /**
   * This saves the user object sent in from SkillzSDK in local storage
   * The user object here is incomplete and will overwrite the actual user, but
   * it is needed in order to launch the SDK
   */
  processNativeUser(userData: Object) {
    this._data.userData = userData;
    if (userData && userData.id) {
      console.debug('CurrentUserStore will set unreadCount');
      Preferences.set({[UNREAD_SUPPORT_MESSAGES_COUNT]: userData.unread_support_message_count});
      RESTStore.updateLocalStorage('user', userData);
    }
  },

  async getUserData(useCache: boolean): Promise {
    if (useCache && this._data && this._data.userData) {
      return this._data.userData;
    } else {
      const userData = await this.fetchUser(useCache);
      console.debug(
        'CurrentUserStore async getUserData, from await fetchUser: ' + JSON.stringify(userData)
      );
      this.updateUserData(userData);
      return this._data.userData;
    }
  },

  getCurrentCachedUserData(): Object {
    return _.get(this._data, 'userData');
  },

  getCurrentCachedPersonalInfo(): Object {
    return _.get(this._data, 'personalInfo');
  },

  async getUserPersonalInfo(): Promise {
    this._data.personalInfo = await this.fetchUserPersonalInfo(true);
    return this._data.personalInfo;
  },

  /**
   * Fetches personal info from server (no cache) and updates the store.
   * Use after login so isSavedAccount() reflects phone_verified etc.
   */
  async refreshUserPersonalInfo(): Promise {
    const info = await this.fetchUserPersonalInfo(false);
    if (info) {
      this._data.personalInfo = info;
      this.emitChangeWithUpdate();
    }
    return this._data.personalInfo;
  },

  async getUserProfile(): Promise {
    this._data.profile = await this.fetchUserProfile();
    return this._data.profile;
  },

  getRandomAvatars(): Array<string> {
    if (!this._data.randomAvatars) {
      this._data.randomAvatars = [];
    }
    return this._data.randomAvatars;
  },

  emitChangeWithUpdate() {
    //TODO: Possibly add DB fetching/setting here.
    this.emitChange();
  },

  updateUser(data: Object): Promise {
    return new Promise((resolve: (Object) => {}, reject: (Object) => {}) => {
      this.getUserData(true).then((user: Object) => {
        if (user.id) {
          NetClient.patchURL(
            'users/' + user.id,
            data,
            SKZAPIManager.OnError.failSilently,
            (error: Object, payload: Object) => {
              if (error || !payload) {
                console.debug(
                  'CurrentUserStore error: ' +
                    JSON.stringify(_.get(error, 'responseObject[0].message'))
                );
                reject(_.get(error, 'responseObject[0].message'));
              } else if (payload.id) {
                //updates the userData store with the updated changes
                const userPatch = _.merge(_.cloneDeep(_.get(this, '_data.userData')), payload);
                this.updateUserData(userPatch);
                if (Platform.OS == 'android' && data.username && data.username != user.username) {
                  ReactUserManager.updateStoredUsername(user.username, data.username);
                }
                if (data.password) {
                  SkillzModule.updateUserPassword(user.username, data.password);
                }
                RESTStore.updateLocalStorage('user', userPatch)
                  .then((responseJson: string) => {
                    resolve(userPatch);
                  })
                  .catch((response: unknown) => {
                    // if it fails we just clear the cache instead of failing, that way the next call to /user will go to server and get the most recent data
                    RESTStore.clearCachedResponse('user');
                    resolve(userPatch);
                  });
                // update vcard with new user data
                GlobalEventEmitter.emit('SetUserInfo', payload, true);
              }
            }
          );
        } else {
          reject(DEFAULT_ERROR_MESSAGE);
        }
      });
    });
  },

  async updateUserPersonalInfoWithCurrentData(data: Object): Promise {
    const currentData = await this.getUserPersonalInfo();
    return this.updateUserPersonalInfo({...currentData, ...data});
  },

  updateUserPersonalInfo(data: Object): Promise {
    return new Promise((resolve: (Object) => {}, reject: (Object) => {}) => {
      this.getUserData(true).then((user: Object) => {
        if (user.id) {
          NetClient.patchURL(
            'users/' + user.id + '/personal',
            data,
            SKZAPIManager.OnError.failSilently,
            (error: Object, payload: Object) => {
              if (error) {
                console.debug(
                  'CurrentUserStore error: ' +
                    JSON.stringify(_.get(error, 'responseObject[0].message'))
                );
                reject(_.get(error, 'responseObject[0].message'));
              } else {
                RESTStore.updateLocalStorage('users/' + user.id + '/personal', payload)
                  .then((responseJson: string) => {
                    this._data.personalInfo = payload;
                    this.emitChangeWithUpdate();
                    resolve(payload);
                  })
                  .catch((response: unknown) => {
                    reject(response);
                  });
              }
            }
          );
        } else {
          reject(DEFAULT_ERROR_MESSAGE);
        }
      });
    });
  },

  async resizeImageForWeb(file, maxWidth, maxHeight, mimeType = 'image/png') {
    const img = new Image();
    const canvas = document.createElement('canvas');

    return new Promise((resolve, reject) => {
      img.onload = () => {
        const ctx = canvas.getContext('2d');
        let {width, height} = img;

        // Calculate aspect ratio and resize
        if (width > maxWidth || height > maxHeight) {
          if (width > height) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          } else {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(resolve, mimeType, 0.8); // Adjust quality if needed
      };
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  },

  async uploadAvatarWeb(userId: string, imageString: string): Promise<Response | undefined> {
    const url = `users/${userId}/avatar`;

    if (APIUtilities.checkValidURL(url)) {
      let fullURL = APIUtilities.generateURL(url);
      const formData = new FormData();
      // Use proxy for cdn image.
      const fetchUrl = AssetHelper.isURL(imageString)
        ? `/api/image?imageString=${imageString}`
        : imageString;
      console.log('CurrentUserStore, uploadAvatarWeb, fetchUrl: ', fetchUrl);

      try {
        const blb = await fetch(fetchUrl, {
          headers: {
            'x-skillz-game-id': gameId,
          },
        })
          .then((res) => res.blob())
          .catch((err) => {
            throw err.message;
          });
        const resizedBlob = await this.resizeImageForWeb(blb, 500, 500); // Resize to max 500x500
        const resizedFile = new File([resizedBlob], 'avatar');
        formData.append('avatar', resizedFile, 'avatar');

        let headers = await NetClient.getDefaultHeaders({});
        // Remove Content-Type header to allow browser to set it automatically
        // based on the media type and boundary of the form data
        delete headers['Content-Type'];
        console.log('CurrentUserStore,', 'uploadAvatarWeb', 'headers', headers);
        console.log('CurrentUserStore', 'uploadAvatarWeb', 'formData', formData);
        const response = await fetch(fullURL, {
          method: 'POST',
          body: formData,
          headers: headers,
        });

        console.log('CurrentUserStore', 'uploadAvatarWeb, ', 'response', response);
        if (!response.ok) {
          const errorResponse = await response.json();
          console.log('CurrentUserStore', 'uploadAvatarWeb, ', 'errorResponse', errorResponse);
          throw errorResponse[0]?.message;
        } else {
          return response;
        }
      } catch (error) {
        console.log('CurrentUserStore', 'uploadAvatarWeb', 'catch block', error);
        throw error;
      }
    } else {
      throw Error('Invalid URL');
    }
  },

  updateAvatar(avatarImagePath: string, avatarURI: string): Promise<Object> {
    if (Platform.OS === 'web') {
      console.log('currentUserStore, updateAvatar, web', avatarImagePath, avatarURI);
      return new Promise(async (resolve, reject) => {
        try {
          const user = await this.getUserData(true);
          const response = await this.uploadAvatarWeb(user.id, avatarImagePath);
          resolve(response);
        } catch (error) {
          reject(error);
        }
      });
    }

    return new Promise((resolve: (response: Object) => void, reject: (error: Object) => void) => {
      ReactUserManager.uploadAvatar(
        avatarImagePath,
        avatarURI,
        (response: unknown, error: unknown) => {
          if (error) {
            console.debug('CurrentUserStore updateAvatar error: ' + JSON.stringify(error));
            reject(error);
          } else {
            try {
              response = JSON.parse(response);
            } catch (err) {
              console.debug('CurrentUserStore parsing error: ' + err);
            }
            resolve(response);
          }
        }
      );
    });
  },

  changePassword(currentPassword: string, newPassword: string): Promise {
    const data = {
      password: newPassword,
      old_password: currentPassword,
    };
    return this.updateUser(data);
  },

  getIdentityChecks(): Promise {
    return new Promise(
      (resolve: (fraudPrompts: Object) => void, reject: (error: unknown) => void) => {
        this.getUserData(true).then((user: Object) => {
          RESTStore.getServerResponse(
            'users/' + user.id + '/identity_checks',
            null,
            true,
            (response: Array<Object>, error: unknown) => {
              const idChecks = [];
              if (error) {
                reject(error);
              } else {
                _.forEach(response, (fraudPrompt: Object) => {
                  idChecks.push(new FraudPrompt(fraudPrompt));
                });
                resolve(idChecks);
              }
            }
          );
        });
      }
    );
  },

  submitIdentityCheck(fieldsToVerify: Object): Promise {
    return new Promise(
      (resolve: (fraudPrompt: Object) => void, reject: (error: unknown) => void) => {
        this.getUserData(true).then((user: Object) => {
          NetClient.patchURL(
            'users/' + user.id + '/identity_checks/' + fieldsToVerify.id,
            fieldsToVerify,
            SKZAPIManager.OnError.failSilently,
            (error: unknown, response: unknown) => {
              if (error) {
                console.debug('CurrentUserStore identity error: ' + JSON.stringify(error));
                reject(error);
              } else {
                resolve(response);
              }
            }
          );
        });
      }
    );
  },

  clearUserCache() {
    this._data = {};
    RESTStore.clearCachedResponse('user');
  },

  clearUserPersonalCache() {
    this.getUserData(true).then((user: Object) => {
      if (user.id) {
        RESTStore.clearCachedResponse('users/' + user.id + '/personal');
      }
    });
  },

  actionTypeHandlers: {
    FETCH_USER(action: string): Promise {
      return new Promise((resolve: (Object) => {}, reject: (Object) => {}) => {
        RESTStore.getServerResponse(
          'user',
          null,
          SKZAPIManager.OnError.promptRetry,
          (payload: Object, error: Object) => {
            if (error || !payload) {
              reject(error.responseObject[0].message);
            } else {
              resolve(payload);
            }
          }
        );
      })
        .then((payload: Object) => {
          if (payload) {
            console.debug('USER: FETCH_USER action, updateUserData: ' + JSON.stringify(payload));
            this.updateUserData(payload);
          }
          this.emitChangeWithUpdate();
        })
        .catch((error: Object) => {
          // TODO: handle error
        });
    },

    FETCH_USER_PERSONAL_INFO(action: String): Promise {
      return new Promise((resolve: (Object) => {}, reject: (Object) => {}) => {
        this.getUserData(true).then((user: Object) => {
          if (user.id) {
            RESTStore.getServerResponse(
              'users/' + user.id + '/personal',
              null,
              SKZAPIManager.OnError.failSilently,
              (payload: Object, error: string) => {
                if (error) {
                  reject(error.responseObject[0].message);
                } else {
                  resolve(payload);
                }
              }
            );
          } else {
            reject(DEFAULT_ERROR_MESSAGE);
          }
        });
      })
        .then((payload: Object) => {
          this._data.personalInfo = payload;
          this.emitChangeWithUpdate();
        })
        .catch((error: String) => {
          // TODO: Handle Error
        });
    },

    FETCH_RANDOM_AVATARS(action: String): Promise {
      return new Promise((resolve: (Object) => {}, reject: (Object) => {}) => {
        this.getUserData(true).then((user: Object) => {
          if (user.id) {
            RESTStore.getServerResponse(
              'users/' + user.id + '/avatar_urls',
              null,
              SKZAPIManager.OnError.failSilently,
              (payload: Object, error: String) => {
                if (error) {
                  reject(error.responseObject[0].message);
                } else {
                  resolve(payload);
                }
              }
            );
          } else {
            reject(DEFAULT_ERROR_MESSAGE);
          }
        });
      })
        .then((payload: Object) => {
          this._data.randomAvatars = payload;
          this.emitChangeWithUpdate();
        })
        .catch((error: Object) => {
          // TODO: Handle Error
        });
    },
  },
  /**
   * Retrieves all user properties from the server
   * @returns Promise that resolves to an array of { key, value } pairs or null in case of an error
   */
  async getAllUserProperties(): Promise<{key: string; value: string}[] | null> {
    try {
      const response = await NetClient.getURL(
        '/user/v1/properties',
        {},
        SKZAPIManager.OnError.failSilently
      );

      if (!response) {
        return null;
      }

      return Array.isArray(response) ? response : [response];
    } catch (error) {
      console.warn('Error fetching all user properties: ', error);
      return null;
    }
  },

  /**
   * Retrieves a specific user property from the server
   * @param propertyKey - The key of the property to retrieve
   * @returns Promise that resolves to the string value if found, otherwise null
   */
  async getUserProperty(propertyKey: string): Promise<string | null> {
    const allProperties = await this.getAllUserProperties();

    if (!allProperties) {
      return null;
    }

    const property = allProperties.find(
      (record: {key: string; value: string}) => record.key === propertyKey
    );
    return property ? property.value : null;
  },

  /**
   * Updates a specific user property on the server
   * @param propertyKey - The key of the property to update
   * @param value - The new value to set for the property
   * @returns Promise that resolves when the update is successful
   * @throws Error if the update fails
   */
  async updateUserProperty(propertyKey: string, value: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.getUserData(true).then((user: Object) => {
        if (user.id) {
          const SkillzAPIManager = NativeModules.SKZAPIManager;
          const url = '/user/v1/properties';
          const patch = {
            key: propertyKey,
            value: value,
          };
          NetClient.patchURL(
            url,
            patch,
            SkillzAPIManager.OnError.failSilently,
            (error: any, response: any) => {
              if (error || !response) {
                console.warn('CurrentUserStore: error updating user property', error);
                reject(error || new Error(DEFAULT_ERROR_MESSAGE));
              } else {
                resolve();
              }
            }
          );
        }
      });
    });
  },
});

export default Store;
